import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDate } from '@pb/shared';
import { ApiError, errorMessage } from '../api/client';
import type { Resource } from '../lib/useResource';

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <div className="spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="state state-error" role="alert">
      <div className="state-title">This could not be loaded</div>
      <p>{errorMessage(error)}</p>
      {onRetry ? (
        <button type="button" className="btn" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="state state-empty">
      <div className="state-title">{title}</div>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

export function NotFound({ what = 'page' }: { what?: string }) {
  return (
    <div className="state">
      <div className="state-title">Not found</div>
      <p>This {what} does not exist or is no longer available.</p>
      <Link className="btn" to="/">
        Go to projects
      </Link>
    </div>
  );
}

export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

interface DataViewProps<T> {
  resource: Resource<T>;
  what?: string;
  loadingLabel?: string;
  children: (data: T) => ReactNode;
}

export function DataView<T>({ resource, what, loadingLabel, children }: DataViewProps<T>) {
  if (resource.data !== undefined) return <>{children(resource.data)}</>;
  if (resource.error) {
    if (isNotFound(resource.error)) return <NotFound what={what} />;
    return <ErrorState error={resource.error} onRetry={() => void resource.reload()} />;
  }
  return <Loading label={loadingLabel} />;
}

export function ErrorAlert({ error }: { error: unknown }) {
  if (!error) return null;
  const inUse = error instanceof ApiError ? error.inUse : null;
  return (
    <div className="alert alert-error" role="alert">
      {errorMessage(error)}
      {inUse ? <InUseList details={inUse} /> : null}
    </div>
  );
}

export function InUseList({ details }: { details: NonNullable<ApiError['inUse']> }) {
  const { projectId } = useParams();
  const groups = details.groups ?? [];
  const presentations = details.presentations ?? [];
  const events = details.events ?? [];
  if (!groups.length && !presentations.length && !events.length) return null;
  const base = projectId ? `/projects/${projectId}` : null;
  return (
    <ul>
      {groups.map((g) => (
        <li key={`g${g.id}`}>
          Group: {base ? <Link to={`${base}/groups/${g.id}`}>{g.name}</Link> : g.name}
        </li>
      ))}
      {presentations.map((p) => (
        <li key={`p${p.id}`}>
          Presentation: {base ? <Link to={`${base}/presentations/${p.id}`}>{p.name}</Link> : p.name}
        </li>
      ))}
      {events.map((e) => (
        <li key={`e${e.id}`}>
          Event: {base ? <Link to={`${base}/events/${e.id}`}>{e.title}</Link> : e.title} ({formatDate(e.date)})
        </li>
      ))}
    </ul>
  );
}
